'use client';

import { ReactNode } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { logos } from '@/shared/assets/images';
import { useOnboardingCsrf } from '@/features/onboarding/hooks/useOnboardingCsrf';
import { useOnboardingNavigationProvider, OnboardingNavigationContext, getLoadingContext } from '@/features/onboarding/hooks/useOnboardingNavigation';
import { useRotatingLoadingMessage, type LoadingMessageContext } from '@/features/onboarding/hooks/useRotatingLoadingMessage';
import { hasCsrfCookie } from '@/features/onboarding/hooks/csrfHelpers';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useGetCsrfTokenMutation, useGetSessionInfoMutation } from '@/api/features/auth';
import { OnboardingErrorBoundary } from '@/features/onboarding/components/OnboardingErrorBoundary';
import { BRAND_BLUE } from '@voley/shared-types/constants';

// Component for rotating loading message display
function RotatingLoadingOverlay({ context }: { context: LoadingMessageContext }) {
  const message = useRotatingLoadingMessage(context);

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <Loader2 className="h-10 w-10 animate-spin mb-4" style={{ color: BRAND_BLUE }} />
      <p className="text-sm text-gray-500 animate-pulse transition-opacity duration-300">
        {message}
      </p>
    </div>
  );
}

function OnboardingContent({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  useOnboardingCsrf();
  
  // Navigation state for smooth transitions
  const navigationState = useOnboardingNavigationProvider();
  const { isNavigating, navigationTarget } = navigationState;

  // CRITICAL FIX: Check if this page should bypass the layout BEFORE running any effects
  // For company-info, get-started, join-agency, signup, and complete-setup pages, render children without layout wrapper
  // These pages handle their own loading states internally
  // NOTE: complete-setup is included because users arrive via magic link without an existing session
  const shouldBypassLayout = useMemo(() => {
    return pathname === '/company-info' ||
           pathname === '/get-started' ||
           pathname === '/join-agency' ||
           pathname === '/signup' ||
           pathname === '/complete-setup';
  }, [pathname]);

  // Readiness gate: ensure auth session and CSRF token are available before enabling interactions
  // ONLY run this for pages that use the layout wrapper (not signup, etc.)
  const [ready, setReady] = useState(false);
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const [getSessionInfo] = useGetSessionInfoMutation();
  const [getCsrfToken] = useGetCsrfTokenMutation();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Skip session check for bypassed pages (signup, etc.)
    if (shouldBypassLayout) {
      queueMicrotask(() => {
        setReady(true); // Mark as ready immediately so no overlay shows
      });
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    const ensureReady = async () => {
      try {
        // Light debounce on navigation to allow cookies to settle
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(resolve, 300);
          signal.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
          });
        });

        if (signal.aborted) return;

        // Check CSRF cookie, fetch if missing
        let hasCsrf = hasCsrfCookie();

        // If no CSRF token, fetch it BEFORE calling session-info
        if (!hasCsrf && !signal.aborted) {
          try {
            await Promise.race([
              getCsrfToken().unwrap(),
              new Promise<never>((_, reject) => setTimeout(() => reject(new DOMException('Aborted', 'AbortError')), 5000)),
              new Promise<never>((_, reject) => {
                signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')));
              }),
            ]);

            // Recheck after fetch
            hasCsrf = hasCsrfCookie();
          } catch {
            // Failed to fetch CSRF token - continue anyway
            if (signal.aborted) return;
          }
        }

        if (signal.aborted) return;

        // Only call getSessionInfo if we have CSRF token
        let hasSession = false;
        if (hasCsrf) {
          try {
            const res = await getSessionInfo().unwrap();
            hasSession = !!res && typeof res === 'object';
          } catch {
            hasSession = false;
          }
        }

        if (!signal.aborted) setReady(hasCsrf && hasSession);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (!signal.aborted) setReady(false);
      }
    };

    ensureReady();

    return () => {
      controller.abort();
    };
  }, [pathname, getSessionInfo, getCsrfToken, shouldBypassLayout]);

  // Loading timeout - 10 seconds max before showing error
  useEffect(() => {
    if (ready || shouldBypassLayout) {
      queueMicrotask(() => {
        setLoadingTimedOut(false);
      });
      return;
    }

    const timer = setTimeout(() => {
      if (!ready) {
        setLoadingTimedOut(true);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [ready, shouldBypassLayout]);

  // Retry function for timed out loading
  const handleRetry = () => {
    setLoadingTimedOut(false);
    setReady(false);
    // Abort current and restart
    abortControllerRef.current?.abort();
    // Re-trigger the effect by updating a dependency would be complex,
    // so we just reload the page for simplicity
    window.location.reload();
  };

  // Early return for bypassed pages - still wrap with navigation context for smooth transitions
  if (shouldBypassLayout) {
    return (
      <OnboardingNavigationContext.Provider value={navigationState}>
        {/* Global navigation overlay for smooth page transitions */}
        {isNavigating && navigationTarget && (
          <RotatingLoadingOverlay context={getLoadingContext(navigationTarget)} />
        )}
        {children}
      </OnboardingNavigationContext.Provider>
    );
  }


  return (
    <OnboardingNavigationContext.Provider value={navigationState}>
      <div className="min-h-screen bg-gray-50">
        {/* Navigation transition overlay */}
        {isNavigating && navigationTarget && (
          <RotatingLoadingOverlay context={getLoadingContext(navigationTarget)} />
        )}
        {!ready && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/80">
            {loadingTimedOut ? (
              <>
                <p className="text-sm text-gray-600 mb-4">Taking longer than expected...</p>
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Retry
                </button>
              </>
            ) : (
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: BRAND_BLUE }} />
            )}
          </div>
        )}
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center">
                {/* Mobile logo: BallOnly */}
                <Image
                  src={logos.ballOnly}
                  alt="Voley"
                  width={40}
                  height={40}
                  className="h-10 w-10 sm:hidden"
                />
                {/* Desktop logo: wordmark */}
                <Image
                  src={logos.voleyT}
                  alt="Voley"
                  width={100}
                  height={26}
                  className="hidden sm:inline h-7 w-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 sm:p-8">
            {children}
          </div>
        </div>
      </div>
    </OnboardingNavigationContext.Provider>
  );
}

export default function OnboardingLayout({ children }: { children: ReactNode }) {
  // NOTE: ReduxProvider and SecureAuthProvider are already provided at the root level
  // via app/layout.tsx -> Providers -> AppProvider. We must NOT nest them here
  // because it causes state conflicts when navigating from onboarding to dashboard.
  // The root SecureAuthProvider will be used, which properly tracks auth state
  // throughout signup → onboarding → dashboard transitions.
  return (
    <OnboardingErrorBoundary stepName="Onboarding">
      <OnboardingContent>{children}</OnboardingContent>
    </OnboardingErrorBoundary>
  );
}